/**
 * A set of keys that several reporters can hold at once: a key stays in the set while any of its
 * reports is open. The missing-resources panel keeps one, and `createCountedReportsContext` makes
 * the others.
 */
import { useCallback, useRef, useState } from 'react';
import { withKey, withoutKey } from '../../utils/keySet.js';
/**
 * The actions keep one identity for the component's life, so an effect that depends on one does
 * not re-run on each change of the set.
 */
export function useCountedReports() {
    const [reported, setReported] = useState(() => new Set());
    /** Open reports per key, written only by the actions below. */
    const reportCounts = useRef(new Map());
    const report = useCallback((key) => {
        if (!key)
            return;
        const count = (reportCounts.current.get(key) ?? 0) + 1;
        reportCounts.current.set(key, count);
        if (count === 1)
            setReported((prev) => withKey(prev, key));
    }, []);
    const clearAll = useCallback((key) => {
        reportCounts.current.delete(key);
        setReported((prev) => withoutKey(prev, key));
    }, []);
    const clear = useCallback((key) => {
        const count = reportCounts.current.get(key);
        if (count === undefined)
            return;
        if (count === 1)
            clearAll(key);
        else
            reportCounts.current.set(key, count - 1);
    }, [clearAll]);
    return { reported, report, clear, clearAll };
}
//# sourceMappingURL=useCountedReports.js.map